/* eslint-disable complexity */
import { DateTime, Duration } from 'luxon';
import { timeStringToDuration } from '@shared/utilities/utilites';
import { SequenceResponse } from '../response/sequence.response';
import { DutyPeriodView } from './duty-period.view';
import { DataItem } from '@shared/models/data-item';

/**
 * Sequence View object. Constructor takes SequenceResponse object and converts it to SequenceView object. Converts numbers to
 * Luxon Durations and gives nulls a default value.
 */
export class SequenceView {
  timestamp: DateTime;
  sequenceNumber: string;
  scheduledFlight: Duration;
  sequenceGreater: Duration;
  sequenceDeadhead: Duration;
  sequencePayCredit: Duration;
  sequenceTotal: Duration;
  sequenceTAFB: Duration;
  sequenceInProgress: boolean;
  sequenceComplete: boolean;
  sequenceFailedCont: boolean;
  sequenceSummaryItems: DataItem[];
  dutyPeriods: DutyPeriodView[] = [];

  constructor(sequenceApi: SequenceResponse) {
    this.timestamp = DateTime.fromISO(sequenceApi.timestamp, { zone: 'utc' });
    this.sequenceNumber = sequenceApi.sequenceNumber || '';
    this.scheduledFlight = Duration.fromObject({
      minutes: sequenceApi.scheduledFlight || 0,
    });
    this.sequenceGreater = Duration.fromObject({
      minutes: sequenceApi.sequenceGreater || 0,
    });
    this.sequenceDeadhead = Duration.fromObject({
      minutes: sequenceApi.sequenceDeadhead || 0,
    });
    this.sequencePayCredit = Duration.fromObject({
      minutes: sequenceApi.sequencePayCredit || 0,
    });
    this.sequenceTotal = Duration.fromObject({
      minutes: sequenceApi.sequenceTotal || 0,
    });
    this.sequenceTAFB = timeStringToDuration(sequenceApi.sequenceTAFB || '');
    this.sequenceInProgress = sequenceApi.sequenceInProgress || false;
    this.sequenceComplete = sequenceApi.sequenceComplete || false;
    this.sequenceFailedCont = sequenceApi.sequenceFailedCont || false;
    this.dutyPeriods = sequenceApi.dutyPeriodSummaries.map(
      (dutyPeriod) => new DutyPeriodView(dutyPeriod, sequenceApi.timestamp)
    );
    this.locateNextLeg();

    this.sequenceSummaryItems = this.createSequenceSummaryItems();
  }

  // Find the first leg that hasn't been flown. Set its isNextLeg property to true.
  private locateNextLeg(): void {
    for (const dutyPeriod of this.dutyPeriods) {
      for (const leg of dutyPeriod.legs) {
        // Deadheads frequently won't show complete, messing up the next logic. So ignore deadheads.
        if (
          !leg.isDeadhead &&
          !leg.isCancelled &&
          leg.flightNumber !== '0000' &&
          leg.removeCode === ''
        ) {
          if (!leg.depTimeActual || (leg.depTimeActual && leg.isDepEstimated)) {
            leg.isNextLeg = true;
            dutyPeriod.containsNextLeg = true;
            return;
          }
        }
      }
    }
  }

  private createSequenceSummaryItems(): DataItem[] {
    return [
      {
        label: 'TAFB',
        value: this.sequenceTAFB?.toFormat('h:mm'),
      },
      { label: 'Greater', value: this.sequenceGreater?.toFormat('h:mm') },
      { label: 'P/C', value: this.sequencePayCredit?.toFormat('h:mm') },
      { label: 'Total', value: this.sequenceTotal?.toFormat('h:mm') },
    ];
  }
}
